import { CheckCircleOutlined, EnvironmentOutlined, LoadingOutlined } from '@ant-design/icons';
import { Select, Space, Spin, Typography } from 'antd';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { firebaseFunctions } from '../../firebase/firebase';
import { FirebaseCallableFunctions } from '../../firebase/firebase.models';

const { Text } = Typography;
const { Option } = Select;

interface LocationOption {
  value: string;
  place_id?: string;
  main_text: string;
  secondary_text: string;
}

interface LocationAutocompleteProps {
  value?: string;
  onChange?: (value: string, coordinates?: { lat: number; lng: number }) => void;
  placeholder?: string;
  size?: 'large' | 'middle' | 'small';
}

export const LocationAutocomplete: React.FC<LocationAutocompleteProps> = ({
  value,
  onChange,
  placeholder,
  size = 'large',
}) => {
  const { t } = useTranslation('wanted-listings');
  const [options, setOptions] = useState<LocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [cachedSearches, setCachedSearches] = useState<Record<string, LocationOption[]>>({});
  const [open, setOpen] = useState(false);

  const placesAutocomplete = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.PLACES_AUTOCOMPLETE);
  const getPlaceDetails = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.GET_PLACE_DETAILS);

  // Wczytaj cache z localStorage
  useEffect(() => {
    const cached = localStorage.getItem('locationSearchCache');
    if (cached) {
      try {
        setCachedSearches(JSON.parse(cached));
      } catch (e) {
        console.error('Error loading cache:', e);
      }
    }
  }, []);

  const fetchSuggestions = useCallback(
    async (searchText: string) => {
      if (!searchText || searchText.length < 2) {
        setOptions([]);
        setError(null);
        setOpen(false);
        return;
      }

      setOpen(true);

      // Sprawdź cache
      if (cachedSearches[searchText]) {
        setOptions(cachedSearches[searchText]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await placesAutocomplete({ input: searchText });
        const predictions = result.data.predictions || [];

        if (predictions.length === 0) {
          setError(t('SUCCESS.LOCATION_NO_RESULTS'));
        }

        const formattedOptions: LocationOption[] = predictions.map((prediction: any) => ({
          value: prediction.description,
          place_id: prediction.place_id,
          main_text: prediction.main_text,
          secondary_text: prediction.secondary_text,
        }));

        setOptions(formattedOptions);

        // Zapisz w cache
        const newCache = { ...cachedSearches, [searchText]: formattedOptions };
        setCachedSearches(newCache);
        localStorage.setItem('locationSearchCache', JSON.stringify(newCache));
      } catch (error: any) {
        console.error('Error fetching autocomplete suggestions:', error);
        setOptions([]);

        // Handle specific error types
        if (error.code === 'failed-precondition') {
          setError(t('SUCCESS.LOCATION_API_CONFIG_ERROR'));
        } else if (error.code === 'internal') {
          setError(t('SUCCESS.LOCATION_API_ERROR'));
        } else {
          setError(t('SUCCESS.LOCATION_NETWORK_ERROR'));
        }
      } finally {
        setLoading(false);
      }
    },
    [placesAutocomplete, t, cachedSearches]
  );

  const debouncedFetch = useCallback(debounce(fetchSuggestions, 300), [fetchSuggestions]);

  const handleSelect = async (selectedValue: string) => {
    if (onChange) {
      // Find selected option
      const selectedOption = options.find((opt) => opt.value === selectedValue);

      if (selectedOption?.place_id) {
        // Get place details to fetch coordinates
        try {
          const details = await getPlaceDetails({ place_id: selectedOption.place_id });
          onChange(selectedValue, details.data.location);
        } catch (error) {
          console.error('Error fetching place details:', error);
          onChange(selectedValue);
        }
      } else {
        onChange(selectedValue);
      }
    }
    setSearchValue('');
    setOpen(false);
  };

  const handleClear = () => {
    if (onChange) {
      onChange('', undefined);
    }
    setSearchValue('');
    setOptions([]);
    setOpen(false);
  };

  return (
    <Select
      showSearch
      value={value}
      searchValue={searchValue}
      placeholder={
        <Space>
          <EnvironmentOutlined />
          {placeholder || t('FORM.LOCATION_PLACEHOLDER')}
        </Space>
      }
      style={{ width: '100%' }}
      size={size}
      defaultActiveFirstOption={false}
      filterOption={false}
      onSearch={(value) => {
        setSearchValue(value);
        debouncedFetch(value);
      }}
      onChange={handleSelect}
      allowClear
      onClear={handleClear}
      suffixIcon={loading ? <Spin indicator={<LoadingOutlined spin />} size="small" /> : <EnvironmentOutlined />}
      open={open}
      notFoundContent={
        loading ? (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Spin size="small" />
            <div style={{ marginTop: '8px' }}>{t('SUCCESS.LOCATION_LOADING')}</div>
          </div>
        ) : error ? (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Text type="danger">{error}</Text>
          </div>
        ) : searchValue.length > 0 && searchValue.length < 2 ? (
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <Text type="secondary">Wpisz minimum 2 znaki</Text>
          </div>
        ) : null
      }
      dropdownStyle={{ maxHeight: 400 }}>
      {options.map((option) => (
        <Option key={option.place_id} value={option.value}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
              <Text strong style={{ flexShrink: 0 }}>
                {option.main_text}
              </Text>
              <Text
                type="secondary"
                style={{ fontSize: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {option.secondary_text}
              </Text>
            </div>
            {value === option.value && (
              <CheckCircleOutlined style={{ color: '#52c41a', flexShrink: 0, marginLeft: '8px' }} />
            )}
          </div>
        </Option>
      ))}
    </Select>
  );
};
